import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CRow,
  CForm,
  CFormGroup,
  CLabel,
  CSelect,
  CButton,
  CLink,
  CAlert,
} from "@coreui/react";
import { useState } from "react";
import store, { RootState } from "../../store";
import { getAbsoluteUrl } from "../../api/urls";
import { successAlert, warningAlert } from "../utils/messages";
import { getRoutePath } from "../../routes";

const REPORT_OPTIONS = [
  {
    label: "Libro de Ventas (RG90)",
    value: "ventas",
    url: "/accounting_reports/sales/",
    filters: { month: true, year: true },
  },
  {
    label: "Libro de Compras (RG90)",
    value: "compras",
    url: "/accounting_reports/purchases/",
    filters: { month: true, year: true },
  },
  {
    label: "Libro Diario",
    value: "diario",
    url: "/accounting_reports/journal/",
    filters: { month: true, year: true },
  },
  {
    label: "Libro Mayor",
    value: "mayor",
    url: "/accounting_reports/ledger/",
    filters: { month: true, year: true },
  },
  {
    label: "Inventario",
    value: "inventario",
    url: "/accounting_reports/inventory/",
    filters: { month: false, year: true },
  },
  {
    label: "Balance General",
    value: "balance",
    url: "/accounting_reports/balance/",
    filters: { month: false, year: true },
  },
  {
    label: "Estado de Resultados",
    value: "resultados",
    url: "/accounting_reports/income_statement/",
    filters: { month: false, year: true },
  },
];

const months = [
  { label: "Enero", value: "01" },
  { label: "Febrero", value: "02" },
  { label: "Marzo", value: "03" },
  { label: "Abril", value: "04" },
  { label: "Mayo", value: "05" },
  { label: "Junio", value: "06" },
  { label: "Julio", value: "07" },
  { label: "Agosto", value: "08" },
  { label: "Septiembre", value: "09" },
  { label: "Octubre", value: "10" },
  { label: "Noviembre", value: "11" },
  { label: "Diciembre", value: "12" },
];

const ReportDashboard = () => {
  const state: RootState = store.getState();
  const accessToken = state.user.accessToken;
  const [selectedReport, setSelectedReport] = useState(null);
  const [month, setMonth] = useState("");
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [loading, setloading] = useState(false);

  const isValidYear = (value: string): boolean => {
    const num = Number(value);
    return !isNaN(num) && Number.isInteger(num) && num >= 1900 && num <= 2100;
  };

  const requestReport = async () => {
    if (!selectedReport) return;

    setloading(true);

    const report = REPORT_OPTIONS.find((r) => r.value === selectedReport);
    if (!report) {
      setloading(false);
      return;
    }

    if (!accessToken) {
      alert("No se encontró el token de acceso.");
      setloading(false);
      return;
    }

    const params = new URLSearchParams();
    if (report.filters.month) params.append("month", month);
    if (report.filters.year) {
      if (!isValidYear(year)) {
        warningAlert("El año ingresado no es válido.");
        setloading(false);
        return;
      }
      params.append("year", year);
    }
    const url = getAbsoluteUrl(`${report.url}?${params.toString()}`);
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: "include",
      });

      const result = await response.json();
      if (!response.ok || result.status === "error") {
        warningAlert(result.detail || "Error al generar el reporte.");
      } else {
        successAlert(
          "El reporte fue solicitado correctamente. Se generará en segundo plano. puedes ver en el historial"
        );
      }
    } catch (error) {
      console.error("Error al solicitar el reporte:", error);
      warningAlert(
        "No se pudo generar el reporte. Inténtalo de nuevo más tarde."
      );
    }

    setloading(false);
  };
  const currentReport = REPORT_OPTIONS.find((r) => r.value === selectedReport);

  return (
    <CContainer fluid>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader>
              <h3>Generar Reportes Contables</h3>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol sm="12">
                  <CAlert color="primary">
                    <h5>
                      ⚠️ Atención: Dependiendo de la cantidad de datos a
                      procesar, la generación de reportes puede demorar un
                      tiempo. Una vez solicitado el reporte mediante el boton
                      "GENERAR REPORTE" puede "Ir al Historial" para comprobar
                      el estado del reporte y donde estará disponible para
                      descargar
                    </h5>
                  </CAlert>
                </CCol>
              </CRow>
              <CForm>
                <CFormGroup>
                  <CLabel>Tipo de Reporte</CLabel>
                  <CSelect
                    value={selectedReport || ""}
                    onChange={(e) => setSelectedReport(e.currentTarget.value)}
                  >
                    <option value="">Seleccione...</option>
                    {REPORT_OPTIONS.map((report) => (
                      <option key={report.value} value={report.value}>
                        {report.label}
                      </option>
                    ))}
                  </CSelect>
                </CFormGroup>

                {currentReport?.filters?.month && (
                  <CFormGroup>
                    <CLabel>Mes</CLabel>
                    <CSelect
                      value={month}
                      onChange={(e) => setMonth(e.currentTarget.value)}
                    >
                      <option value="">Seleccione...</option>
                      {months.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </CSelect>
                  </CFormGroup>
                )}

                {currentReport?.filters?.year && (
                  <CFormGroup>
                    <CLabel>Año</CLabel>
                    <input
                      type="number"
                      className="form-control"
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      required
                    />
                  </CFormGroup>
                )}

                <CButton
                  color="primary"
                  disabled={!selectedReport || loading}
                  onClick={requestReport}
                >
                  Generar Reporte
                </CButton>
                <CLink
                  className="ml-2 btn btn-success"
                  color="success"
                  to={getRoutePath("accountingReportsHistory")}
                >
                  Ir al Historial
                </CLink>
              </CForm>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>
  );
};

export default ReportDashboard;
